<template>
  <main class="min-h-screen bg-slate-800 text-white">
    <!-- HERO -->

    <section
      class="grid items-center gap-12 px-6 py-14 lg:grid-cols-2 lg:gap-16 lg:px-20"
    >
      <!-- LEFT -->

      <div class="relative z-20">
        <p
          class="mb-6 text-sm font-bold uppercase tracking-[0.25em] text-rtcblue-400"
        >
          {{ $t("home.eyebrow") }}
        </p>

        <h1
          class="max-w-4xl text-4xl font-black leading-tight sm:text-5xl lg:text-7xl"
        >
          {{ $t("home.title") }}
        </h1>

        <p
          class="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:mt-8 sm:text-xl sm:leading-9"
        >
          {{ $t("home.intro") }}
        </p>
        <div
          class="relative z-20 mt-10 flex flex-col gap-4 sm:flex-row sm:gap-6"
        >
          <router-link
            to="/platform"
            class="inline-flex rounded-full bg-rtcblue-500 px-8 py-4 font-bold transition hover:bg-rtcblue-400"
          >
            {{ $t("home.explore") }}
          </router-link>

          <router-link
            to="/solutions"
            class="inline-flex rounded-full border border-white/20 bg-white/5 px-8 py-4 font-bold text-white transition hover:bg-white/10"
          >
            {{ $t("home.how") }}
          </router-link>
        </div>
      </div>

      <!-- RIGHT -->

      <div class="relative">
        <!-- glow -->

        <div
          class="absolute -left-10 top-10 h-72 w-72 rounded-full bg-rtcblue-500/20 blur-3xl"
        ></div>

        <!-- dashboard -->

        <div
          class="relative overflow-hidden rounded-[32px] border border-white/15 bg-white/10 p-6 shadow-2xl backdrop-blur-xl"
        >
          <div class="mb-6 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-bold">{{ $t("home.netOverview") }}</h3>

              <p class="text-sm text-slate-400">{{ $t("home.netSub") }}</p>
            </div>

            <div
              class="flex items-center gap-2 rounded-full bg-rtcblue-500/20 px-4 py-2 text-sm font-bold text-rtcblue-300"
            >
              <span class="relative flex h-2.5 w-2.5">
                <span
                  class="absolute inline-flex h-full w-full animate-ping rounded-full bg-rtcblue-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex h-2.5 w-2.5 rounded-full bg-rtcblue-400"
                ></span>
              </span>
              {{ $t("home.live") }}
            </div>
          </div>

          <!-- map -->

          <div class="rounded-3xl border border-white/15 bg-white/10 p-6">
            <img
              src="/assets/network-overview.png"
              alt="Network overview"
              class="h-72 w-full rounded-2xl object-cover"
            />
          </div>

          <!-- stats -->

          <div class="mt-6 grid gap-6 md:grid-cols-3">
            <div class="rounded-2xl bg-white/10 p-6">
              <p class="text-sm text-slate-400">{{ $t("home.overflow") }}</p>

              <h3 class="mt-3 text-4xl font-black text-rtcblue-400">↗ 28%</h3>
            </div>
            <div class="rounded-2xl bg-white/10 p-6">
              <p class="text-sm text-slate-400">{{ $t("home.storage") }}</p>

              <h3 class="mt-3 text-4xl font-black text-rtcblue-400">↗ 73%</h3>
            </div>
            <div class="rounded-2xl bg-white/10 p-6">
              <p class="text-sm text-slate-400">{{ $t("home.response") }}</p>

              <h3 class="mt-3 text-4xl font-black text-rtcblue-400">↗ 54 %</h3>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CUSTOMERS -->

    <section class="border-y border-white/10 bg-slate-800 py-14">
      <div class="px-6 text-center lg:px-20">
        <p
          class="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400"
        >
          {{ $t("home.customersTitle") }}
        </p>
      </div>

      <div class="logo-marquee mt-8 overflow-hidden py-2">
          <div class="logo-track flex w-max gap-4">
            <a
              v-for="(c, i) in marqueeCustomers"
              :key="i"
              :href="c.url"
              target="_blank"
              rel="noopener noreferrer"
              :title="c.name"
              class="flex h-24 w-40 shrink-0 items-center justify-center rounded-xl bg-white p-4 shadow-lg transition duration-200 hover:-translate-y-1 hover:shadow-xl"
            >
              <img
                :src="`/assets/customers/${c.file}.png`"
                :alt="c.name"
                class="max-h-14 max-w-full object-contain"
              />
            </a>
          </div>
        </div>
    </section>

    <!-- PROJECT DASHBOARD -->

    <section
      id="dashboard"
      class="bg-slate-800 px-6 py-16 text-white lg:px-20"
    >
      <div class="mx-auto max-w-7xl">
        <div class="mb-12 max-w-3xl">
          <p
            class="text-sm font-bold uppercase tracking-[0.25em] text-rtcblue-400"
          >
            {{ $t("home.dashEyebrow") }}
          </p>

          <h2
            class="mt-4 text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl"
          >
            {{ $t("home.dashTitle") }}
          </h2>

          <p class="mt-6 text-xl leading-8 text-slate-300">
            {{ $t("home.dashIntro") }}
          </p>
        </div>

        <div class="grid gap-6 lg:grid-cols-3">
          <DashboardCard
            :label="$t('home.orgLabel')"
            :title="$t('home.orgName')"
            :description="$t('home.orgDesc')"
          />

          <div
            class="rounded-3xl bg-white/10 p-8 shadow-xl transition duration-200 hover:-translate-y-1 hover:bg-white/[0.14]"
          >
            <p class="text-sm font-bold text-slate-400">
              {{ $t("home.stageLabel") }}
            </p>
            <h3 class="mt-3 text-2xl font-black">{{ $t("home.stageName") }}</h3>
            <div class="mt-6 h-3 rounded-full bg-white/10">
              <div class="h-3 w-7/12 rounded-full bg-rtcblue-500"></div>
            </div>
            <p class="mt-4 text-sm font-semibold text-slate-400">
              {{ $t("home.stageProgress") }}
            </p>
          </div>

          <div
            class="rounded-3xl bg-white/10 p-8 shadow-xl transition duration-200 hover:-translate-y-1 hover:bg-white/[0.14]"
          >
            <p class="text-sm font-bold text-slate-400">
              {{ $t("home.readinessLabel") }}
            </p>
            <h3 class="mt-3 text-5xl font-black text-rtcblue-400">74%</h3>
            <p class="mt-4 text-slate-300">
              {{ $t("home.readinessDesc") }}
            </p>
          </div>
        </div>

        <div class="mt-8 grid gap-6 lg:grid-cols-4">
          <div
            class="rounded-3xl bg-white/10 p-6 shadow-lg transition duration-200 hover:-translate-y-1 hover:bg-white/[0.14]"
          >
            <p class="text-sm text-slate-400">{{ $t("home.mData") }}</p>
            <h4 class="mt-3 text-2xl font-black text-rtcblue-400">
              {{ $t("home.mDataV") }}
            </h4>
          </div>

          <div
            class="rounded-3xl bg-white/10 p-6 shadow-lg transition duration-200 hover:-translate-y-1 hover:bg-white/[0.14]"
          >
            <p class="text-sm text-slate-400">{{ $t("home.mSwmm") }}</p>
            <h4 class="mt-3 text-2xl font-black text-amber-500">
              {{ $t("home.mSwmmV") }}
            </h4>
          </div>

          <div
            class="rounded-3xl bg-white/10 p-6 shadow-lg transition duration-200 hover:-translate-y-1 hover:bg-white/[0.14]"
          >
            <p class="text-sm text-slate-400">{{ $t("home.mPlc") }}</p>
            <h4 class="mt-3 text-2xl font-black text-rtcblue-400">
              {{ $t("home.mPlcV") }}
            </h4>
          </div>

          <div
            class="rounded-3xl bg-white/10 p-6 shadow-lg transition duration-200 hover:-translate-y-1 hover:bg-white/[0.14]"
          >
            <p class="text-sm text-slate-400">{{ $t("home.mCtrl") }}</p>
            <h4 class="mt-3 text-2xl font-black text-sky-400">
              {{ $t("home.mCtrlV") }}
            </h4>
          </div>
        </div>
      </div>
    </section>

    <section id="workflow" class="bg-slate-800 px-6 py-16 text-white lg:px-20">
      <div class="mx-auto max-w-7xl">
        <div class="mb-12 max-w-3xl">
          <p
            class="text-sm font-bold uppercase tracking-[0.25em] text-rtcblue-400"
          >
            {{ $t("home.wfEyebrow") }}
          </p>

          <h2
            class="mt-4 text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl"
          >
            {{ $t("home.wfTitle") }}
          </h2>

          <p class="mt-6 text-xl leading-8 text-slate-300">
            {{ $t("home.wfIntro") }}
          </p>
        </div>

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <StageCard
            v-for="(s, i) in $t('home.stages')"
            :key="i"
            :title="s.title"
            :description="s.desc"
            :status="stageMeta[i].status"
            :readiness="stageMeta[i].readiness"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import DashboardCard from "@/components/DashboardCard.vue";
import StageCard from "@/components/StageCard.vue";

export default {
  name: "HomeView",

  components: {
    DashboardCard,
    StageCard,
  },

  computed: {
    marqueeCustomers() {
      return [...this.customers, ...this.customers];
    },
  },

  data() {
    return {
      customers: [
        {
          file: "junglinster",
          name: "Commune de Junglinster",
          url: "https://www.junglinster.lu",
        },
        {
          file: "lenningen",
          name: "Commune de Lenningen",
          url: "https://www.lenningen.lu",
        },
        {
          file: "list",
          name: "Luxembourg Institute of Science and Technology",
          url: "https://www.list.lu",
        },
        {
          file: "manternach",
          name: "Commune de Manternach",
          url: "https://www.manternach.lu",
        },
        {
          file: "ses",
          name: "SES – Syndicat des Eaux du Sud",
          url: "https://www.ses-eau.lu",
        },
        { file: "siden", name: "SIDEN", url: "https://www.siden.lu" },
        { file: "sidere", name: "SIDERE", url: "https://sidere.lu" },
        {
          file: "stadtbredimus",
          name: "Commune de Stadtbredimus",
          url: "https://www.stadtbredimus.lu",
        },
        {
          file: "step-bettembourg",
          name: "STEP Bettembourg",
          url: "https://www.bettembourg.lu",
        },
        {
          file: "tandel",
          name: "Commune de Tandel",
          url: "https://www.tandel.lu",
        },
        {
          file: "wormeldange",
          name: "Commune de Wormeldange",
          url: "https://www.wormeldange.lu",
        },
      ],

      stageMeta: [
        { status: "complete", readiness: 100 },
        { status: "complete", readiness: 92 },
        { status: "active", readiness: 74 },
        { status: "planned", readiness: 38 },
        { status: "planned", readiness: 22 },
        { status: "planned", readiness: 10 },
      ],
    };
  },
};
</script>

<style scoped>
.logo-marquee {
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    #000 7%,
    #000 93%,
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 7%,
    #000 93%,
    transparent
  );
}

.logo-track {
  animation: logo-scroll 60s linear infinite;
}

.logo-marquee:hover .logo-track {
  animation-play-state: paused;
}

@keyframes logo-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}
</style>
